import React, { useEffect, useState } from 'react';
import { searchMedicationLists } from '../Async/MedicationListsAsync';
import { MedicationListsDataTable } from '../Components/DataTable/MedicationListsDataTable';
import { TwoPanelLayout } from '../Components/TwoPanelLayout';
import { MedicationListsSearchFilter } from '../Contracts/MedicationListsSearchFilter.Request';
import { MedicationListsSearchResult } from '../Contracts/MedicationListsSearchResult.Response';
import { useAsync } from '../Hooks/useAsync';
import { useDebounce } from '../Hooks/useDebounce';
import { useHttpConfig } from '../Shared/HttpRequestExecutor';
import { LookupIdentifier } from '../Shared/SharedModels';
import {
    isMedListFiltered,
    MedicationListFilter,
    MedListFilterState,
    useMedicationListFilter
} from './MedicationListFilter';
import styles from './MedicationLists.module.scss';

export interface MedicationProps {
    query: string;
    filtersActive: boolean;
    onEditClick(row: LookupIdentifier): void;
    onAddMedicationList?(): void;
    /**
     * By calling children as a method such as this, we gain the ability for a external system to call back
     * into the medication list component.
     */
    children?(medicationListHelpers: { refresh(): void }): React.ReactNode;
}

export const MedicationLists = (props: MedicationProps): JSX.Element => {
    const httpConfig = useHttpConfig();
    const debounceQuery = useDebounce(props.query);
    const medicationListsAsync = useAsync({
        defaultValue: null,
        httpConfig,
        getApi: searchMedicationLists
    });

    const doMedListRequest = async (filter: MedListFilterState) => {
        const apiFilter: MedicationListsSearchFilter = {
            status: filter.status?.value === 'All' ? null : filter.status?.value ?? null,
            types: filter.types.map(it => it.value)
        };

        await medicationListsAsync.getApi(filter.limit === 0 ? 0 : filter.limit + 1, debounceQuery, apiFilter);
        setFiltered(debounceQuery !== '' || isMedListFiltered(filter));
    };

    const filterState = useMedicationListFilter(doMedListRequest);

    const [filtered, setFiltered] = useState(props.query !== '' || isMedListFiltered(filterState.state));

    const getMedicationLists = async () => {
        doMedListRequest(filterState.state);
    };

    const medicationLists: MedicationListsSearchResult[] = medicationListsAsync.data ?? [];

    useEffect(() => {
        getMedicationLists();
    }, [debounceQuery]);

    return (
        <div className={styles.dashboard}>
            <TwoPanelLayout
                leftPanelOpen={props.filtersActive}
                noPadding
                leftPanel={<MedicationListFilter {...filterState} />}
                contents={
                    <>
                        <MedicationListsDataTable
                            filtered={filtered}
                            medicationLists={medicationLists}
                            loadCount={medicationListsAsync.loadCount}
                            limit={filterState.state.limit}
                            hasSearchString={false}
                            loadStatus={medicationListsAsync.status}
                            onAddMedicationList={props.onAddMedicationList}
                            onDelete={() => {
                                getMedicationLists();
                            }}
                            onEditClick={props.onEditClick}
                        />
                        {!!props.children &&
                            props.children({
                                refresh: getMedicationLists
                            })}
                    </>
                }
            />
        </div>
    );
};
